"use client";

import BasicLayout from "@components/BasicLayout";
import DebugInfo from "@components/Debug/Info";
import ProfileHeader from "@components/ProfileHeader";
import PromotionLoginPrompt from "@components/Promotions/LoginPrompt";
import TripHead from "@components/TripHead";
import UpfrontContainer from "@components/UpfrontCollection/Container";
import Error from "@hudson/ui/components/Error";
import MinorButton from "@hudson/ui/components/FormFields/ButtonMinor";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import Success from "@hudson/ui/components/Success";
import { parentRedirect } from "@hudson/ui/lib/utils";
import { event } from "@lib/analytics/events";
import FrameConfig from "@lib/classes/FrameConfig";
import Session from "@lib/session";
import { preloadLocations } from "@lib/universal/services/find_locations";
import useContent from "@lib/use_content";
import useUpdateFrameConfig from "@lib/use_update_frame_config";
import { Place } from "@universal/internal/Place";
import { findLocations } from "@universal/services/find_locations";
import locationPredictionSelected from "@universal/services/location_prediction_selected";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";

const Landing = ({}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [frameConfigLoading, frameConfigError, frameConfigDebug] = useUpdateFrameConfig();
  const [errorMessage, setErrorMessage] = useState<string>(frameConfigError);
  const [successMessage, setSuccessMessage] = useState<string>(
    searchParams?.get("successMsg") || "",
  );
  const { data, loading } = useContent(["beErrSessionExpired", "beMakeOneWayResButton"]);

  preloadLocations();

  useEffect(() => {
    if (frameConfigError) setErrorMessage(frameConfigError);
  }, [frameConfigError]);

  // Set session expiration message
  useEffect(() => {
    if (searchParams?.get("session_expired"))
      setErrorMessage(data?.content.beErrSessionExpired || "");
  }, []);

  // If this was displayed as a widget we need to redirect to a new page to show services.
  function submit() {
    const frameConfig = new FrameConfig();
    parentRedirect(`${frameConfig.bookingUrl}?_bedst=2`);
  }

  // Analytics event
  useEffect(() => {
    if (!frameConfigLoading) event("view_landing_page");
  }, [frameConfigLoading]);

  const Layout = searchParams?.get("widget") ? React.Fragment : BasicLayout;

  return frameConfigLoading ? (
    <ConcentricLoader className="max-w-2xl px-4 py-10 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <Layout>
      <>
        {!Session.isSecondLeg() && <ProfileHeader />}

        {errorMessage && (
          <div className="relative">
            <Error className="mb-3" message={errorMessage} scrollTo heavyStyle />
            {frameConfigDebug && (
              <DebugInfo className="absolute right-5 -top-3" dialogContent={frameConfigDebug}>
                Why?
              </DebugInfo>
            )}
          </div>
        )}

        {successMessage && (
          <Success className="mb-3" message={successMessage} heavyStyle scrollTo />
        )}
        <TripHead className="mb-3 text-2xl font-bold md:text-3xl" noLocations noDate />
        {Session.isSecondLeg() && (
          <MinorButton
            noBorder
            padding="pl-0 pr-2 py-2"
            className="mb-3 underline"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M5.965 4.904l9.131 9.131a6.5 6.5 0 00-9.131-9.131zm8.07 10.192L4.904 5.965a6.5 6.5 0 009.131 9.131zM4.343 4.343a8 8 0 1111.314 11.314A8 8 0 014.343 4.343z"
                  clipRule="evenodd"
                />
              </svg>
            }
            onClick={() => {
              Session.removeRoundtrip();
              router.push(`/${window.siteId}/book/checkout`);
            }}
            type="button"
          >
            {data?.content.beMakeOneWayResButton}
          </MinorButton>
        )}

        {!Session.isSecondLeg() && <PromotionLoginPrompt className="mb-1.5" />}

        <UpfrontContainer
          findLocations={findLocations}
          limitLocations={searchParams?.get("widget") ? 4 : undefined}
          locationPredictionSelected={locationPredictionSelected}
          locationPredictionRenderer={Place}
          onSubmit={searchParams?.get("widget") ? submit : undefined}
          servicesShown={false}
          servicesUrl={`/${window.siteId}/custom/universal/book/services${
            Session.isSecondLeg() ? "?leg_index=1" : ""
          }`}
          noMap={!!searchParams?.get("widget")}
        />
      </>
    </Layout>
  );
};

export default Landing;
